import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Context } from "../../../contexts/Store";
import { baseConfig } from "../../../utils/config";
import Text from "../../../components/Text";
import file from "../../../assets/images/file-icon.svg";

export default function ContentAddModal({
    isOpen,
    onClose,
    type,
    reload,
    setReload,
    slug,
    topicId,
    contentType,
    contentEditId,
}) {
    const { state } = useContext(Context);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const dropAreaRef = useRef(null);
    let access_token = state.user_details.access_token;
    const [errorMessage, setErrorMessage] = useState("");

    const [content, setContent] = useState({
        title: "",
        image: null,
        description: "",
        video_link: "",
        isLoading: false,
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setContent({
            ...content,
            [name]: value,
        });
    };

    const handleImageUpload = (file) => {
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert(
                    "Image size exceeds 10MB limit. Please choose a smaller image."
                );

                return;
            }

            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            setContent({
                ...content,
                image: file,
            });
        }
    };

    const handleSaveCourse = async () => {
        setContent((prev) => ({ ...prev, isLoading: true }));
        setErrorMessage("");
        try {
            const formData = new FormData();
            formData.append("title", content.title);
            formData.append("course", slug);
            formData.append("content_type", "video");
            formData.append("description", content.description);
            formData.append("video_link", content.video_link);

            if (content.image) {
                formData.append("thumbnail", content.image);
            }

            if (topicId) {
                formData.append("topic", topicId);
            }

            let url = `free-courses/contents/`;
            let method = baseConfig.post;

            if (contentType === "edit" && contentEditId) {
                url = `free-courses/contents/${contentEditId.id}/`;
                method = baseConfig.put;
            }

            const response = await method(url, formData, {
                headers: {
                    Authorization: "Bearer " + access_token,
                    "Content-Type": "multipart/form-data",
                },
            });

            const { StatusCode } = response.data;
            if (StatusCode === 6000) {
                handleClear();
                setReload(!reload);
                onClose();
            } else {
                setErrorMessage(
                    response?.data?.message || "Failed to add course"
                );
            }
        } catch (error) {
            if (error?.response?.data?.code === "token_not_valid") {
                    localStorage.clear();
                    window.location = "/";
                }
            setErrorMessage(
                error?.response?.data?.message ||
                    "An error occurred while saving the course"
            );
        } finally {
            setContent((prev) => ({ ...prev, isLoading: false }));
        }
    };

    useEffect(() => {
        if (contentType === "edit" && contentEditId) {
            setContent({
                title: contentEditId.title || "",
                description: contentEditId.description || "",
                video_link: contentEditId.video_link || "",
                image: null,
                isLoading: false,
            });

            if (contentEditId.thumbnail) {
                setImagePreview(contentEditId.thumbnail);
            }
        }
    }, [contentType, contentEditId]);

    const handleClear = () => {
        setContent({
            title: "",
            isLoading: false,
        });
    };

    if (!isOpen) return null;

    return (
        <>
            <ModalOverlay>
                <ModalContainer>
                    <ModalHeader>
                        <ModalTitle>
                            {type === "edit" ? "Edit Content" : " Add Content"}
                        </ModalTitle>
                    </ModalHeader>
                    <ImageUploadSection>
                        {imagePreview && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <CloseButton
                                    onClick={() => setImagePreview(null)}
                                >
                                    ×
                                </CloseButton>
                            </div>
                        )}
                        <UploadBox
                            ref={dropAreaRef}
                            className={imagePreview ? "has-image" : ""}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {imagePreview ? (
                                <ImagePreviewContainer>
                                    <ImageDiv>
                                        <img src={imagePreview} alt="Preview" />
                                    </ImageDiv>
                                    <ImageFileName>
                                        {content.image?.name || "HTML.jpg"}
                                        <ImageFileSize>
                                            {content.image?.size
                                                ? `${(
                                                      content.image.size /
                                                      (1024 * 1024)
                                                  ).toFixed(1)}MB`
                                                : "9MB"}
                                        </ImageFileSize>
                                    </ImageFileName>
                                </ImagePreviewContainer>
                            ) : (
                                <>
                                    <IconContainer>
                                        <DocumentIcon />
                                    </IconContainer>
                                    <Text
                                        color="#9D9D9D"
                                        fontSize="24px"
                                        fontWeight="400"
                                        textAlign="center"
                                    >
                                        Drag and Drop image or
                                    </Text>
                                    <ChooseButton>Choose image</ChooseButton>
                                    <FileInfo>
                                        File type support: png, jpeg, 1600X1200
                                        or heigher recommended size: 10MB
                                    </FileInfo>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png, image/jpeg"
                                onChange={(e) =>
                                    handleImageUpload(e.target.files[0])
                                }
                                style={{ display: "none" }}
                            />
                        </UploadBox>
                    </ImageUploadSection>
                    <FormGroup>
                        <Label>Title</Label>
                        <Input
                            type="text"
                            name="title"
                            placeholder="Enter title"
                            value={content.title}
                            onChange={handleInputChange}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Video URL</Label>
                        <Input
                            type="text"
                            name="video_link"
                            placeholder="Enter title"
                            value={content.video_link}
                            onChange={handleInputChange}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Description</Label>
                        <TextArea
                            name="description"
                            placeholder="Enter description"
                            value={content.description}
                            onChange={handleInputChange}
                        />
                    </FormGroup>
                    {errorMessage && (
                        <ErrorMessage>
                            <ErrorIcon>⚠️</ErrorIcon>
                            {errorMessage}
                        </ErrorMessage>
                    )}
                    <ModalFooter>
                        <CancelButton
                            onClick={() => {
                                handleClear();
                                onClose();
                            }}
                        >
                            Cancel
                        </CancelButton>
                        <AddButton onClick={handleSaveCourse}>
                            {content?.isLoading ? "Saving..." : "Save"}
                        </AddButton>
                    </ModalFooter>
                </ModalContainer>
            </ModalOverlay>
        </>
    );
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.57);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background-color: white;
    border-radius: 10px;
    width: 600px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.2),
        0px 5px 15px rgba(0, 0, 0, 0.1);
    padding: 20px;
`;

const ModalHeader = styled.div`
    border-bottom: 1px solid #c1c1c1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
    font-size: 24px;
    font-weight: 500;
    color: #010101;
    margin-bottom: 20px;
`;
const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 16px;
    color: #000;
`;

const FormGroup = styled.div`
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
`;

const Input = styled.input`
    width: calc(100% - 0px);
    padding: 12px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 16px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #5a2d8f;
    }

    &::placeholder {
        color: #9d9d9d;
        font-size: 14px;
        font-weight: 300;
    }
`;

const ModalFooter = styled.div`
    padding-top: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const CancelButton = styled.button`
    background-color: white;
    color: #333;
    border: 1px solid #ccc;
    padding: 12px 24px;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const AddButton = styled.button`
    background-color: #5a2d8f;
    color: white;
    border: none;
    padding: 12px 40px;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: #4a2575;
    }
`;
const ImageUploadSection = styled.div`
    margin-bottom: 20px;
`;

const UploadBox = styled.div`
    border: 1px dashed #c1c1c1;
    border-radius: 10px;
    padding: 30px;
    text-align: center;
    cursor: pointer;
    transition: border 0.3s ease;
    &:hover {
        border-color: #5a2d8f;
    }
    &.highlight {
        border-color: #5a2d8f;
        background-color: rgba(90, 45, 143, 0.05);
    }
    &.has-image {
        padding: 0;
        border: none;
    }
`;

const ImagePreviewContainer = styled.div`
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    img {
        width: 100%;
        height: 180px;
        object-fit: cover;
        display: block;
    }
`;

const ImageFileName = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f8f8f8;
    font-size: 14px;
    color: #333;
`;

const ImageFileSize = styled.span`
    color: #888;
    font-size: 12px;
`;
const IconContainer = styled.div`
    margin-bottom: 15px;
    display: flex;
    justify-content: center;
`;

const DocumentIcon = styled.div`
    width: 60px;
    height: 60px;
    background-color: #f0e6ff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &:after {
        content: "";
        position: absolute;
        width: 30px;
        height: 40px;
        background: url(${file}) no-repeat center;
    }
`;

const ChooseButton = styled.span`
    display: inline-block;
    background-color: #f5f5f5;
    color: #5a2d8f;
    border-radius: 5px;
    padding: 8px 16px;
    margin: 10px 0;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    transition: background-color 0.3s;

    &:hover {
        background-color: #e9e9e9;
    }
`;

const FileInfo = styled.p`
    font-size: 14px;
    color: #999;
    margin: 0 auto;
    width: 60%;
`;
const TextArea = styled.textarea`
    width: calc(100% - 0px);
    padding: 12px;
    border: 1px solid #ccc;
    border-radius: 5px;
    min-height: 100px;
    font-size: 16px;
    resize: vertical;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: #5a2d8f;
    }
    &::placeholder {
        color: #9d9d9d;
        font-size: 14px;
        font-weight: 300;
    }
`;
const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: #999;
    &:hover {
        color: #333;
    }
`;
const ImageDiv = styled.div`
    width: 100%;
    height: 260px;
    img {
        width: 100%;
        height: 100%;
        object-fit: fill;
    }
`;

const ErrorMessage = styled.div`
    background-color: #fff2f2;
    color: #d32f2f;
    padding: 12px 16px;
    border-left: 4px solid #d32f2f;
    margin-bottom: 20px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    font-size: 14px;
    animation: fadeIn 0.3s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ErrorIcon = styled.span`
    margin-right: 10px;
    font-size: 16px;
`;
